// SSE event schema (spec §4.4). Every status write publishes one of these to the bus (RT-1).
// Each event is tagged with `domain` for scoped fan-out (null = broadcast / cross-domain).
import { z } from "zod";
import { DomainSchema, PreprintStatusSchema, ReviewerStatusSchema, COI_VERDICTS } from "./enums.js";

const base = {
  domain: DomainSchema.nullable(),
  at: z.string(), // ISO timestamp
};

export const PreprintUpdatedSchema = z.object({
  type: z.literal("preprint.updated"),
  preprintId: z.string(),
  status: PreprintStatusSchema.optional(),
  ...base,
});

export const ReviewerUpdatedSchema = z.object({
  type: z.literal("reviewer.updated"),
  proposalId: z.string(),
  preprintId: z.string().optional(),
  status: ReviewerStatusSchema.optional(),
  ...base,
});

export const CoiCompletedSchema = z.object({
  type: z.literal("coi.completed"),
  proposalId: z.string(),
  preprintId: z.string().optional(),
  verdict: z.enum(COI_VERDICTS),
  reason: z.string().optional(),
  ...base,
});

export const NotificationSchema = z.object({
  type: z.literal("notification"),
  userId: z.string(), // user email (the identity key)
  kind: z.string(),
  message: z.string(),
  ...base,
});

export const SseEventSchema = z.discriminatedUnion("type", [
  PreprintUpdatedSchema,
  ReviewerUpdatedSchema,
  CoiCompletedSchema,
  NotificationSchema,
]);

export type PreprintUpdated = z.infer<typeof PreprintUpdatedSchema>;
export type ReviewerUpdated = z.infer<typeof ReviewerUpdatedSchema>;
export type CoiCompleted = z.infer<typeof CoiCompletedSchema>;
export type NotificationEvent = z.infer<typeof NotificationSchema>;
export type SseEvent = z.infer<typeof SseEventSchema>;
export type SseEventType = SseEvent["type"];

export function parseSseEvent(raw: unknown): SseEvent {
  return SseEventSchema.parse(raw);
}
